import { useEffect, useState } from 'react';
import Banner from 'components/banner/banner';
import BillingContactPicker from 'components/billingContacts/BillingContactPicker';
import { billingContactPrefillFrom } from 'components/billingContacts/billingContactForm';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import BillingExternalView from './BillingExternalView';
import BillingMemberView from './BillingMemberView';

const VIEW_SEARCH = 'search';
const VIEW_MEMBER = 'member';
const VIEW_EXTERNAL = 'external';

/**
 * « Ajouter un contact de facturation » (JBWEB-1285): the search (8.3), then
 * the member picked (8.4) or a contact without an account (8.5). The Admin
 * shows one modal at a time, so these are views of this modal, not modals.
 */
const ModalAddBillingContact = () => {
  const { toggle } = useModal();
  const { language, modalData } = UseContext();
  const text = (key) => language.getValue(`companyBillingContacts.${key}`, '');
  // Read once: « Inviter sur Jobbiz » replaces « modalData » for the next modal.
  const [idCompany] = useState(() => modalData?.idCompany);
  const [view, setView] = useState(VIEW_SEARCH);
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState('loading');
  const [readCount, setReadCount] = useState(0);
  const [pickedMember, setPickedMember] = useState(null);
  const [typedValues, setTypedValues] = useState({});

  useEffect(() => {
    if (!idCompany) return undefined;
    let isCurrent = true;
    setStatus('loading');
    ApiDatabase.getCompanyBillingContacts(
      { idCompany },
      (response) => {
        if (!isCurrent) return;
        setMembers(response?.data?.members ?? []);
        setStatus('ready');
      },
      (error) => {
        if (!isCurrent || error?.name === 'CanceledError') return;
        setStatus('error');
      },
      // Its own key: the card's read shares the default one and would be
      // cancelled by this one, leaving the card loading for good.
      { abortKey: 'admin/companies/:idCompany/billingContacts|picker' },
    );
    return () => {
      isCurrent = false;
    };
  }, [idCompany, readCount]);

  const title = text('add.title');

  const membersFallback =
    status === 'error' ? (
      <div className={'flex flex-col gap-2'}>
        <Banner type={'warning'}>{text('add.loadError')}</Banner>
        <button
          type={'button'}
          onClick={() => setReadCount((previous) => previous + 1)}
          className={'self-start text-sm text-yellow-600 underline'}
        >
          {text('add.retry')}
        </button>
      </div>
    ) : status === 'loading' ? (
      <div className={'px-3 py-2 text-gray-500'}>{text('add.loading')}</div>
    ) : null;

  if (view === VIEW_MEMBER) {
    return (
      <BillingMemberView
        idCompany={idCompany}
        member={pickedMember}
        title={title}
        onBack={() => setView(VIEW_SEARCH)}
      />
    );
  }

  if (view === VIEW_EXTERNAL) {
    return (
      <BillingExternalView
        idCompany={idCompany}
        initialValues={typedValues}
        title={title}
        onBack={() => setView(VIEW_SEARCH)}
      />
    );
  }

  return (
    <>
      <Modalheader hide={toggle} hideShow>
        {title}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-3'}>
          <BillingContactPicker
            members={members}
            membersFallback={membersFallback}
            query={query}
            onQueryChange={setQuery}
            onPickMember={(member) => {
              setPickedMember(member);
              setView(VIEW_MEMBER);
            }}
            onAddExternal={(typed) => {
              setTypedValues(billingContactPrefillFrom(typed));
              setView(VIEW_EXTERNAL);
            }}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
          {text('actions.cancel')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAddBillingContact;
